import type {
  ResolvedScope,
  Scope,
  ScopeTree,
  ScopeTreeBrand,
  ScopeTreeOrganization,
} from "../types";
import { dedupeStringArray, isValidScope } from "./scopeStorage";

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && b.every((id) => new Set(a).has(id));
}

function collectAllChatbotIds(tree: ScopeTree | null | undefined): string[] {
  const ids = new Set<string>();
  for (const org of tree?.organizations || []) {
    for (const brand of org.brands || []) {
      for (const id of brand.chatbot_ids || []) ids.add(id);
    }
    for (const id of org.ungrouped_chatbot_ids || []) ids.add(id);
  }
  return [...ids];
}

function collectOrgChatbotIds(org: ScopeTreeOrganization | null | undefined): string[] {
  const ids = new Set<string>();
  for (const brand of org?.brands || []) {
    for (const id of brand.chatbot_ids || []) ids.add(id);
  }
  for (const id of org?.ungrouped_chatbot_ids || []) ids.add(id);
  return [...ids];
}

function normalizeBotIds(tree: ScopeTree | null | undefined, ids: readonly string[]): string[] {
  const selected = new Set(dedupeStringArray(ids));
  const all = collectAllChatbotIds(tree);
  return all.length ? all.filter((id) => selected.has(id)) : [...selected];
}

export function normalizeScopeForTree(tree: ScopeTree | null | undefined, scope: Scope): Scope {
  if (!isValidScope(scope) || scope.type !== "bots") return scope;
  const ids = normalizeBotIds(tree, scope.ids);
  if (!ids.length) return { type: "all" };
  const all = collectAllChatbotIds(tree);
  if (all.length && sameMembers(ids, all)) return { type: "all" };
  if (ids.length === 1) return { type: "bot", id: ids[0] };
  for (const org of tree?.organizations || []) {
    if (collectOrgChatbotIds(org).length > 1 && sameMembers(ids, collectOrgChatbotIds(org))) {
      return { type: "org", id: org.id };
    }
    for (const brand of org.brands || []) {
      const brandIds = dedupeStringArray(brand.chatbot_ids);
      if (brandIds.length > 1 && sameMembers(ids, brandIds)) {
        return { type: "brand", id: brand.id };
      }
    }
  }
  return { type: "bots", ids };
}

function findBrand(
  tree: ScopeTree | null | undefined,
  brandId: number,
): { brand: ScopeTreeBrand; org: ScopeTreeOrganization } | null {
  for (const org of tree?.organizations || []) {
    const brand = (org.brands || []).find((candidate) => candidate.id === brandId);
    if (brand) return { brand, org };
  }
  return null;
}

function findBot(
  tree: ScopeTree | null | undefined,
  botId: string,
): { org: ScopeTreeOrganization; brand: ScopeTreeBrand | null } | null {
  for (const org of tree?.organizations || []) {
    if ((org.ungrouped_chatbot_ids || []).includes(botId)) return { org, brand: null };
    const brand = (org.brands || []).find((item) => (item.chatbot_ids || []).includes(botId));
    if (brand) return { org, brand };
  }
  return null;
}

export function resolveScope(
  tree: ScopeTree | null | undefined,
  scope: Scope | null | undefined,
): ResolvedScope {
  if (!scope) {
    return {
      chatbotIds: collectAllChatbotIds(tree),
      label: ["__SCOPE_I18N_scopePickerAllChatbots__"],
    };
  }
  if (scope.type === "all") {
    const count = tree?.organizations?.length ?? 0;
    return {
      chatbotIds: collectAllChatbotIds(tree),
      label: [
        count > 1
          ? "__SCOPE_I18N_scopePickerAllCustomers__"
          : "__SCOPE_I18N_scopePickerAllChatbots__",
      ],
    };
  }
  if (scope.type === "org") {
    const org = tree?.organizations?.find((item) => item.id === scope.id);
    return org
      ? { chatbotIds: collectOrgChatbotIds(org), label: [org.name] }
      : { chatbotIds: [], label: ["__SCOPE_I18N_scopePickerOrgUnavailable__"] };
  }
  if (scope.type === "brand") {
    const located = findBrand(tree, scope.id);
    return located
      ? {
          chatbotIds: [...(located.brand.chatbot_ids || [])],
          label: [located.org.name, located.brand.name],
        }
      : { chatbotIds: [], label: ["__SCOPE_I18N_scopePickerBrandUnavailable__"] };
  }
  if (scope.type === "bot") {
    const located = findBot(tree, scope.id);
    if (!located) return { chatbotIds: [scope.id], label: [scope.id] };
    return {
      chatbotIds: [scope.id],
      label: [located.org.name, ...(located.brand ? [located.brand.name] : []), scope.id],
    };
  }
  const chatbotIds = normalizeBotIds(tree, scope.ids);
  return {
    chatbotIds,
    label: chatbotIds.length === 1 ? [chatbotIds[0]] : [`${chatbotIds.length} chatbots`],
  };
}
